import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import NotificationBell from "@/components/NotificationBell";
import NavDot from "@/components/NavDot";
import { useEmployeeActionCounts, useAdminActionCounts } from "@/hooks/useActionCounts";
import PolicyBanner from "@/components/PolicyBanner";
import LetterBanner from "@/components/LetterBanner";
import {
  LayoutDashboard, User, ClipboardList, ShieldCheck, GraduationCap,
  FileText, MessageSquareWarning, LogOut, Menu, X, BadgeCheck, CalendarDays, CalendarClock, Users, Clock, Bell, Wallet, BookOpenCheck, FilePenLine, Plane
} from "lucide-react";

const NAV = [
  { to: "/", label: "My Dashboard", icon: LayoutDashboard, end: true },
  { to: "/attendance", label: "My Attendance", icon: Clock },
  { to: "/notifications", label: "My Notifications", icon: Bell },
  { to: "/policies", label: "My Policies", icon: BookOpenCheck, badge: true },
  { to: "/letters", label: "My Letters", icon: FilePenLine },
  { to: "/profile", label: "My Profile", icon: User },
  { to: "/team", label: "My Team", icon: Users, roOnly: true },
  { to: "/responsibilities", label: "My Responsibilities", icon: ClipboardList },
  { to: "/authorizations", label: "My Authorizations", icon: ShieldCheck },
  { to: "/qualifications", label: "My Qualifications", icon: GraduationCap },
  { to: "/leave", label: "My Leave", icon: CalendarDays },
  { to: "/on-duty", label: "My On Duty", icon: Plane },
  { to: "/grievances", label: "My Grievances", icon: MessageSquareWarning },
  { to: "/holidays", label: "Holiday Calendar", icon: CalendarClock },
  { to: "/documents", label: "My Documents", icon: FileText },
  { to: "/payslips", label: "My Payslips", icon: Wallet },
  { to: "/requests", label: "Request Correction", icon: MessageSquareWarning },
];

export default function EmployeeLayout({ children }) {
  const { user, logout, hasEmployeeProfile, setViewMode } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };
  const { data: team } = useQuery({ queryKey: ["myTeam"], queryFn: async () => (await api.get("/my/team")).data });
  const items = NAV.filter((n) => !n.roOnly || team?.is_reporting_officer);
  const counts = useEmployeeActionCounts();
  // The CEO's other hat: show a dot on "Admin Portal" when something is waiting there.
  const adminCounts = useAdminActionCounts(hasEmployeeProfile);
  const adminTotal = Object.values(adminCounts).reduce((a, b) => a + (Number(b) || 0), 0);
  // Same cache key as PolicyBanner, so the badge and the banner share one request.
  const { data: unreadData } = useQuery({
    queryKey: ["unreadNotifications"], queryFn: async () => (await api.get("/my/notifications/unread-count")).data,
    refetchInterval: 60000, retry: false,
  });
  const unreadCount = unreadData?.count || 0;
  const { data: policyStatus } = useQuery({
    queryKey: ["myPolicyStatus"], queryFn: async () => (await api.get("/my/policies/status")).data,
    refetchInterval: 5 * 60 * 1000,
  });

  return (
    <div className="App flex bg-background">
      <aside className={cn(
        "brand-sidebar fixed z-40 inset-y-0 left-0 w-64 flex flex-col transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )} data-testid="emp-sidebar">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
          <img src="/intenciv-mark.webp" alt="IntenCiv" className="h-9 w-9 rounded-md object-contain bg-white/95 p-0.5" />
          <div className="leading-tight">
            <p className="text-white font-serif-display text-lg tracking-tight">IntenCiv</p>
            <p className="text-teal text-[10px] tracking-[0.2em] uppercase">Employee Portal</p>
          </div>
          <button className="ml-auto lg:hidden text-white/70" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              data-testid={`emp-nav-${n.label.split(" ")[1]?.toLowerCase() || "dashboard"}`}
              className={({ isActive }) => cn(
                "nav-link relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-white/75",
                isActive && "nav-link-active"
              )}>
              <n.icon className="h-5 w-5 shrink-0" strokeWidth={2} />{n.label}
              <NavDot count={counts[n.to]} className="ml-auto" />
              {n.badge && policyStatus?.pending > 0 && (
                <span className={cn("ml-auto rounded-full px-1.5 min-w-[1.25rem] text-center text-[11px] font-bold text-white", policyStatus.overdue ? "bg-red-500" : "bg-amber-500")}>{policyStatus.pending}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-2 px-2 mb-3">
            <div className="h-9 w-9 rounded-full bg-teal/20 grid place-items-center text-white text-sm font-semibold">{user?.name?.[0]?.toUpperCase() || "E"}</div>
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-white/50 text-[11px] flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Employee</p>
            </div>
          </div>
          {hasEmployeeProfile && (
            <button onClick={() => { setViewMode("admin"); navigate("/"); }} data-testid="switch-to-admin"
              className="w-full flex items-center gap-2 rounded-md px-3 py-2 mb-1 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 transition-colors">
              <ShieldCheck className="h-4 w-4" /> Back to Admin Portal <NavDot count={adminTotal} className="ml-auto" />
            </button>
          )}
          <button onClick={doLogout} data-testid="emp-logout-btn"
            className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white/75 hover:bg-white/10 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden text-navy" onClick={() => setOpen(true)}><Menu className="h-6 w-6" /></button>
          <img src="/intenciv-logo.webp" alt="IntenCiv Diagnostics" className="h-7 object-contain hidden sm:block" />
          <span className="ml-auto overline text-slate-400 hidden sm:inline">Your HR Self-Service</span>
          {hasEmployeeProfile && (
            <button onClick={() => { setViewMode("admin"); navigate("/"); }}
              className="ml-auto sm:ml-4 text-xs font-semibold text-navy border border-teal rounded-md px-2.5 py-1.5 hover:bg-teal/10">
              Admin Portal <NavDot count={adminTotal} className="ml-1.5" />
            </button>
          )}
          <div className="ml-2 flex items-center"><NotificationBell /></div>
        </header>
        <main className="p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8 max-w-[1400px] mx-auto fade-in"><LetterBanner /><PolicyBanner />{children}</main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 flex justify-around pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]" data-testid="emp-bottom-nav">
        {[
          { to: "/", label: "Home", icon: LayoutDashboard, end: true },
          { to: "/attendance", label: "Attendance", icon: Clock },
          { to: "/notifications", label: "Alerts", icon: Bell, dot: unreadCount > 0 },
          { to: "/profile", label: "Profile", icon: User },
        ].map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end}
            className={({ isActive }) => cn("relative flex flex-col items-center gap-0.5 px-3 py-1 text-[11px]", isActive ? "font-semibold text-[#205991]" : "text-slate-500")}>
            <t.icon className="h-[22px] w-[22px]" />
            {t.dot && <span className="absolute top-0.5 right-2 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-white" aria-label="Unread notifications" />}
            {t.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
